import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import "../Assets/styles/customPhoneSelect.css";
import "react-phone-number-input/style.css";

import { RecaptchaVerifier, setPersistence, browserSessionPersistence } from "firebase/auth";
import { auth } from "../firebase";
import { useAuthService } from "../Context/AuthContext";
import AuthScreen from "../Components/Auth Components/AuthScreen";
import EnterPhoneNumber from "../Components/Auth Components/EnterPhoneNumber";
import EnterOtpCode from "../Components/Auth Components/EnterOtpCode";

import Header from "../Components/GenericComponents/Header";
import CreateNewUser from "../Components/Auth Components/CreateNewUser";
import { useNavigate } from "react-router";
import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import { isMobile } from "react-device-detect";
import { useTranslate } from "@tolgee/react";
import { useDefaultCountry } from "../hooks/useGeolocationDb";
import { createSearchParams, useLocation, useSearchParams } from "react-router-dom";
import LocalStorage from "../Helpers/LocalStorage";
import { useLinksquaredService } from "../Context/LinksquaredContext";
import appleAuthService from "../Helpers/appleAuthService";
import { useNotify } from "../Helpers/Notifications";
import { authBridgeLogin } from "../API/authBridge";
import { needsSsoOnboarding } from "../Helpers/userProfile";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("RO");
  const [otp, setOtp] = useState("");
  const [wrongCode, setWrongCode] = useState(false);
  const { t } = useTranslate();
  const isElectron = !!window.electron;
  const {
    signInWithPhone,
    verifyOtpCodeAndSignIn,
    getCurrentUserNoRedirect,
    getCurrentUser,
    getTranslationsLanguages,
    getCustomToken,
    getMicrosoftSSOLoginLink,
    getCallTranslationsLanguages,
    getGoogleSSOLoginLink,
    signInWithCustomAcessToken,
    disableAuthObserver,
    disableAuthCallback,
  } = useAuthService();

  const [phase, setPhase] = useState(1);
  const [defaultCountry] = useDefaultCountry();
  const navigate = useNavigate();
  const location = useLocation();
  const confirmationRes = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { getInvitedBy, deepLink, setDeepLink } = useLinksquaredService();
  const { notifySuccess, notifyError } = useNotify();
  const backTo = location.state?.backTo;
  const conferenceSignIn = location.state?.conferenceSignIn;

  const navigateAfterLogin = (fallbackPath) => {
    navigate(backTo || fallbackPath, { replace: true });
  };

  const setFirebaseTokens = async (completion) => {
    const authUser = auth.currentUser;
    if (authUser) {
      LocalStorage.setAuthenthicationToken(authUser.accessToken);
      LocalStorage.setRefreshToken(authUser.stsTokenManager.refreshToken);
    }
    completion();
  };

  const initializeReCaptcha = () => {
    if (window.recaptchaVerifier == null) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {},
        }
      );
    }
  };

  const resetRechaptcha = () => {
    window.recaptchaVerifier.recaptcha.reset();
  };

  const reinitializeRecaptchaContainer = () => {
    window.recaptchaVerifier = null;
    let element = document.getElementById("recaptcha-container");
    let parent = element.parentElement;
    parent.removeChild(element);
    let newElement = document.createElement("div");
    newElement.setAttribute("id", "recaptcha-container");
    parent.appendChild(newElement);
  };

  const onSignInSubmit = (e, onOtpSent) => {
    if (window.recaptchaVerifier == null) {
      initializeReCaptcha();
    } else {
      resetRechaptcha();
    }
    LocalStorage.removeSSOFlag();
    LocalStorage.removeBridgeOptedOut();
    setIsLoading(true);
    signInWithPhone(
      phone,
      window.recaptchaVerifier,
      function (confirmationResult) {
        setIsLoading(false);
        confirmationRes.current = confirmationResult;
        if (onOtpSent) {
          onOtpSent();
        } else {
          setPhase(3);
        }
        notifySuccess(`${t("login.code_sent")}`);
      },
      function (error) {
        notifyError(`${t("general.something_wrong")}`);
        console.log(error, "err");
        setIsLoading(false);
      }
    );
  };

  const verifyPhoneOtp = (otp, onSuccess, onError) => {
    disableAuthCallback.current = true;
    setIsLoading(true);
    verifyOtpCodeAndSignIn(
      confirmationRes.current,
      otp,
      function (response) {
        reinitializeRecaptchaContainer();
        handleCodeResponse();
        onSuccess();
      },
      function (error) {
        disableAuthCallback.current = false;
        setIsLoading(false);
        console.log(error);
        notifyError(`${t("general.something_wrong")}`);
        onError();
      }
    );
  };

  const resendPhoneOtp = (onOtpSent) => {
    onSignInSubmit(null, onOtpSent);
  };

  const handleVerifyCode = (otp) => {
    disableAuthCallback.current = true;
    setIsLoading(true);
    verifyOtpCodeAndSignIn(
      confirmationRes.current,
      otp,
      function (response) {
        reinitializeRecaptchaContainer();
        setWrongCode(false);
        handleCodeResponse();
      },
      function (error) {
        disableAuthCallback.current = false;
        setOtp("");
        setWrongCode(true);
        console.log(error);
        notifyError(`${t('general.something_wrong')}`);
        setIsLoading(false);
      }
    );
  };

  const handleCodeResponse = () => {
    if (searchParams.get("desktop_login") === "true") {
      disableAuthCallback.current = false;
      getCustomToken(
        (response) => {
          setSearchParams((prev) => {
            prev.set("access_token", response.data.authentication_token);
          });

          navigate({
            pathname: "/desktop_login_success",
            search: createSearchParams(searchParams).toString(),
          });
          setIsLoading(false);
        },
        (error) => {
          setIsLoading(false);
          setPhase(4);
        }
      );
    } else {
      getCurrentUserNoRedirect(
        function (response) {
          disableAuthCallback.current = false;
          // Maybe here fetch current user
          setIsLoading(false);
          navigateAfterLogin("/");
        },
        function (error) {
          if (
            error?.response?.status === 404 ||
            error?.response?.status === 401
          ) {
            setIsLoading(false);
            setPhase(4);
          }
        }
      );
    }
  };

  const handleResendOTP = () => {
    onSignInSubmit();
  };

  const checkReady = () => {
    if (otp.length === 6) {
      handleVerifyCode(otp);
    }
  };

  const openSSOLink = (link) => {
    if (isElectron && window.electron?.openSSOLoginPage) {
      window.electron.openSSOLoginPage(link);
    } else {
      window.location = link;
    }
  };

  const onLoginMicrosoftSubmit = () => {
    LocalStorage.removeBridgeOptedOut();
    LocalStorage.setSSOFlag(true);
    if (isElectron && disableAuthObserver) disableAuthObserver.current = true;
    getInvitedBy();

    getMicrosoftSSOLoginLink(
      false,
      (response) => {
        openSSOLink(response.data.redirect_url);
      },
      () => {
        if (isElectron && disableAuthObserver) disableAuthObserver.current = false;
        notifyError(`${t("general.something_wrong")}`);
      }
    );
  };

  const onLoginAppleSubmit = () => {
    LocalStorage.removeBridgeOptedOut();
    LocalStorage.setSSOFlag(true);
    getInvitedBy();
    openSSOLink(appleAuthService.getAuthorizationUrl());
  };

  const onLoginGoogleSubmit = () => {
    LocalStorage.removeBridgeOptedOut();
    LocalStorage.setSSOFlag(true);
    if (isElectron && disableAuthObserver) disableAuthObserver.current = true;
    // This will set the cookie
    getInvitedBy();

    getGoogleSSOLoginLink(
      false,
      (response) => {
        openSSOLink(response.data.redirect_url);
      },
      () => {
        if (isElectron && disableAuthObserver) disableAuthObserver.current = false;
        notifyError(`${t("general.something_wrong")}`);
      }
    );
  };

  const handleEmailAuthSuccess = (token, refresh_token) => {
    LocalStorage.setAuthenthicationToken(token);
    LocalStorage.setRefreshToken(refresh_token);
    LocalStorage.setSSOFlag(true);
    LocalStorage.setAuthMethod("email");
    disableAuthCallback.current = false;
    getCurrentUserNoRedirect(
      (response) => {
        setIsLoading(false);
        navigateAfterLogin(
          response.data.user.language != null && !needsSsoOnboarding(response.data.user)
            ? "/"
            : "/onboarding"
        );
      },
      (error) => {
        setIsLoading(false);
        if (error?.response?.status === 404 || error?.response?.status === 401) {
          setPhase(4);
        }
      }
    );
  };

  const onPhoneBlockClick = () => {
    if (window.electron?.openExternalUrl) {
      window.electron.openExternalUrl(`${process.env.REACT_APP_ELECTRON_TO_WEB_URL}&auth_step=phone`);
    }
  };

  useEffect(() => {
    if (!isElectron || !window.electron?.responseFromSSoAuthWindow) return;

    let handled = false;
    window.electron.responseFromSSoAuthWindow(({ token, refresh_token }) => {
      if (handled) return;
      handled = true;
      LocalStorage.setSSOFlag(true);
      LocalStorage.setAuthenthicationToken(token);
      LocalStorage.setRefreshToken(refresh_token);
      if (disableAuthObserver) disableAuthObserver.current = false;
      getCurrentUser(
        (response) => {
          navigateAfterLogin(
            response.data.user.language != null && !needsSsoOnboarding(response.data.user)
              ? "/"
              : "/onboarding"
          );
        },
        () => {}
      );
    });
  }, []);

  useEffect(() => {
    if (!isElectron || !deepLink) return;
    const receivedUrl = new URL(deepLink);
    const access_token = receivedUrl.searchParams.get("access_token");
    if (!access_token) return;

    setIsLoading(true);
    disableAuthCallback.current = true;
    signInWithCustomAcessToken(
      access_token,
      () => {
        disableAuthCallback.current = false;
        getCurrentUser(
          (response) => {
            setIsLoading(false);
            setDeepLink(null);
            navigateAfterLogin(
              response.data.user.language != null && !needsSsoOnboarding(response.data.user)
                ? "/"
                : "/onboarding"
            );
          },
          () => {
            setIsLoading(false);
            setDeepLink(null);
          }
        );
      },
      () => {
        disableAuthCallback.current = false;
        setIsLoading(false);
        setDeepLink(null);
      }
    );
  }, [deepLink]);

  useEffect(() => {
    const existingToken = LocalStorage.getAuthenticationToken();
    if (existingToken) return;
    if (LocalStorage.getBridgeOptedOut()) return;

    setIsLoading(true);

    authBridgeLogin("app").then((result) => {
      if (result?.token && result?.refresh_token) {
        // Store tokens
        LocalStorage.setAuthenthicationToken(result.token);
        LocalStorage.setRefreshToken(result.refresh_token);
        LocalStorage.setSSOFlag(true);

        getCurrentUserNoRedirect(
          (response) => {
            setIsLoading(false);
            navigateAfterLogin(
              response.data.user.language != null && !needsSsoOnboarding(response.data.user)
                ? "/"
                : "/onboarding"
            );
          },
          () => setIsLoading(false)
        );
      } else {
        setIsLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    checkReady();
  }, [otp]);

  useEffect(() => {
    if (phase <= 2) return;
    getTranslationsLanguages(() => {}, () => {});
    getCallTranslationsLanguages(() => {}, () => {});
  }, [phase]);

  useEffect(() => {
    if (isMobile && !conferenceSignIn) {
      window.location.replace("https://emotii.ai");
    }
    if (conferenceSignIn) {
      setPersistence(auth, browserSessionPersistence).catch(() => {});
      sessionStorage.setItem("conference_scoped_auth", "1");
      if (backTo) {
        sessionStorage.setItem("conference_sign_in_back_to", backTo);
      }
    }
    reinitializeRecaptchaContainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phase1 = (
    <AuthScreen
      email={email}
      setEmail={setEmail}
      onLoginGoogleSubmit={onLoginGoogleSubmit}
      onLoginMicrosoftSubmit={onLoginMicrosoftSubmit}
      onLoginAppleSubmit={onLoginAppleSubmit}
      phone={phone}
      setPhone={setPhone}
      setCountryCode={setCountryCode}
      onSigninSubmit={onSignInSubmit}
      defaultCountry={defaultCountry}
      onEmailAuthSuccess={handleEmailAuthSuccess}
      verifyPhoneOtp={verifyPhoneOtp}
      resendPhoneOtp={resendPhoneOtp}
      setIsLoading={setIsLoading}
      onElectronPhoneRedirect={isElectron ? onPhoneBlockClick : undefined}
    />
  );

  const phase2 = (
    <>
      <Header />
      <EnterPhoneNumber
        phone={phone}
        setPhone={setPhone}
        setCountryCode={setCountryCode}
        onSigninSubmit={onSignInSubmit}
        defaultCountry={defaultCountry}
        onLoginMicrosoftSubmit={onLoginMicrosoftSubmit}
        onLoginGoogleSubmit={onLoginGoogleSubmit}
        onLoginAppleSubmit={onLoginAppleSubmit}
      />
    </>
  );

  const phase3 = (
    <>
      <Header />
      <EnterOtpCode
        otp={otp}
        setOtp={setOtp}
        wrongCode={wrongCode}
        handleVerifyCode={handleVerifyCode}
        handleResendOTP={handleResendOTP}
      />
    </>
  );

  const phase4 = (
    <>
      <Header />
      <CreateNewUser
        setIsLoading={setIsLoading}
        countryCode={countryCode}
        setFirebaseTokens={setFirebaseTokens}
      />
    </>
  );

  const phases = { 1: phase1, 2: phase2, 3: phase3, 4: phase4 };

  return (
    <SpinnerComponent isLoading={isLoading}>
      <FirstWrapper>
        <LoginPageContainer>{phases[phase]}</LoginPageContainer>
      </FirstWrapper>
    </SpinnerComponent>
  );
};

export default LoginPage;

const LoginPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

`;

const FirstWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  overflow: auto;
  background: ${({ theme }) => theme.menu.background};
`;
